import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { useCart } from "../context/CartContext";
import { theme } from "../components/theme";

// Add to .env:
//   REACT_APP_EMAILJS_SERVICE_ID=...
//   REACT_APP_EMAILJS_TEMPLATE_ID=...
//   REACT_APP_EMAILJS_PUBLIC_KEY=...

const COUNTRIES = [
  { value: "Kosovo", label: "Kosovë", shipping: 0 },
  { value: "Albania", label: "Shqipëri", shipping: 5 },
  { value: "Macedonia", label: "Maqedoni", shipping: 5 },
];

export default function Checkout() {
  const { cart, subtotal, clearCart } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "Kosovo",
    city: "",
    address: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const shipping =
    COUNTRIES.find((c) => c.value === form.country)?.shipping ?? 0;
  const total = subtotal + shipping;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const cartSummary = cart
    .map(
      (i) =>
        `• ${i.name} (${i.variant} shots) × ${i.qty} = €${(i.price * i.qty).toFixed(2)}`,
    )
    .join("\n");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.country || !form.city || !form.address) {
      setError("Ju lutemi plotësoni të gjitha fushat e detyrueshme.");
      return;
    }
    if (cart.length === 0) {
      setError("Shporta juaj është bosh.");
      return;
    }
    setSending(true);
    setError("");

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          customer_name: form.name,
          customer_email: form.email || "—",
          customer_phone: form.phone || "—",
          customer_country: form.country,
          customer_city: form.city,
          customer_address: form.address,
          order_items: cart
            .map(
              (i) =>
                `• ${i.name} (${i.variant} shots) × ${i.qty} = €${(i.price * i.qty).toFixed(2)}`,
            )
            .join("\n"),
          order_subtotal: `€${subtotal.toFixed(2)}`,
          order_shipping:
            shipping === 0 ? "Falas (Kosovë)" : `€${shipping.toFixed(2)}`,
          order_total: `€${total.toFixed(2)}`,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );
      clearCart();
      setSent(true);
    } catch (err) {
      setError("Ndodhi një gabim. Ju lutemi provoni sërisht.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Page>
        <SuccessBox>
          <SuccessIcon>✓</SuccessIcon>
          <h2>Porosia u dërgua!</h2>
          <p>Faleminderit, {form.name}! Do t'ju kontaktojmë së shpejti.</p>
          <BackBtn onClick={() => navigate("/")}>
            ← Kthehu në faqe kryesore
          </BackBtn>
        </SuccessBox>
      </Page>
    );
  }

  return (
    <Page>
      <PageTitle>Përfundo Porosinë</PageTitle>
      <Layout>
        {/* ── Form ── */}
        <Form onSubmit={handleSubmit}>
          <SectionLabel>Të dhënat tuaja</SectionLabel>

          <Field>
            <Label>
              Emri i plotë <Req>*</Req>
            </Label>
            <Input
              value={form.name}
              onChange={set("name")}
              placeholder="p.sh. Arjeta Krasniqi"
              required
              type="name"
            />
          </Field>
          <Row>
            <Field>
              <Label>
                Numri i telefonit <Optional>(opsionale)</Optional>
              </Label>
              <Input
                type="phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+383 44 000 000"
              />
            </Field>
            <Field>
              <Label>
                Email <Optional>(opsionale)</Optional>
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="email@shembull.com"
              />
            </Field>
          </Row>

          <Row>
            <Field>
              <Label>
                Shteti <Req>*</Req>
              </Label>
              <Select value={form.country} onChange={set("country")} required>
                {COUNTRIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>
                Qyteti <Req>*</Req>
              </Label>
              <Input
                value={form.city}
                onChange={set("city")}
                placeholder="p.sh. Prishtinë"
                required
              />
            </Field>
          </Row>

          <Field>
            <Label>
              Adresa e plotë <Req>*</Req>
            </Label>
            <Textarea
              value={form.address}
              onChange={set("address")}
              placeholder="Rruga, numri i shtëpisë, kodi postar..."
              rows={3}
              required
            />
          </Field>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <SubmitBtn type="submit" disabled={sending}>
            {sending ? "Duke dërguar..." : `Porosit — €${total.toFixed(2)}`}
          </SubmitBtn>
        </Form>

        {/* ── Order Summary ── */}
        <Summary>
          <SectionLabel>Përmbledhja</SectionLabel>
          {cart.map((item) => (
            <SummaryItem key={`${item.id}_${item.variant}`}>
              <SummaryImg src={item.imageUrl} alt={item.name} />
              <SummaryInfo>
                <span>{item.name}</span>
                <small>
                  {item.variant} shots × {item.qty}
                </small>
              </SummaryInfo>
              <SummaryPrice>€{(item.price * item.qty).toFixed(2)}</SummaryPrice>
            </SummaryItem>
          ))}

          <SummaryDivider />
          <SummaryRow>
            <span>Nëntotali</span>
            <span>€{subtotal.toFixed(2)}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Transporti</span>
            <ShippingVal free={shipping === 0}>
              {shipping === 0 ? "Falas 🎉" : `€${shipping.toFixed(2)}`}
            </ShippingVal>
          </SummaryRow>
          <SummaryDivider />
          <SummaryTotal>
            <span>Totali</span>
            <span>€{total.toFixed(2)}</span>
          </SummaryTotal>

          {shipping === 0 && (
            <FreeShippingNote>✓ Transporti falas në Kosovë</FreeShippingNote>
          )}
        </Summary>
      </Layout>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: ${theme.colors.bg};
  padding: 7rem 5vw 5rem;
`;
const PageTitle = styled.h1`
  font-family: "Playfair Display", serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  color: ${theme.colors.textPrimary};
  font-style: italic;
  margin-bottom: 3rem;
  text-align: center;
`;
const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 4rem;
  align-items: center;
  margin: auto;
  max-width: 1100px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "image"
      "content";
  }
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;
const SectionLabel = styled.p`
  font-family: ${theme.fonts.mono};
  font-size: 0.65rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-bottom: 0.5rem;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;
const Row = styled.div`
  display: flex;
  gap: 1rem;
`;
const Label = styled.label`
  font-size: 0.82rem;
  color: ${theme.colors.textSecondary};
`;
const Req = styled.span`
  color: #89d146;
`;
const Optional = styled.span`
  font-size: 0.72rem;
  color: ${theme.colors.textMuted};
  margin-left: 4px;
`;
const inputBase = `
  background: ${theme.colors.bgSurface};
  border: 1px solid ${theme.colors.borderSubtle};
  border-radius: ${theme.radius.md};
  padding: 1.2rem 1rem;
  color: ${theme.colors.textPrimary};
  font-size: 0.9rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
  &:focus { border-color: #89d146; }
  &::placeholder { color: ${theme.colors.textMuted}; }
`;
const Input = styled.input`
  ${inputBase}
`;
const Select = styled.select`
  ${inputBase}
  cursor: pointer;
  option {
    background: #1a1612;
  }
`;
const Textarea = styled.textarea`
  ${inputBase} resize: vertical;
`;
const ErrorMsg = styled.p`
  color: #e05c5c;
  font-size: 0.85rem;
`;
const SubmitBtn = styled.button`
  padding: 1.1rem;
  border-radius: 999px;
  border: none;
  background: #89d146;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  font-family: ${theme.fonts.body};
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-top: 0.5rem;
  &:hover:not(:disabled) {
    background: #7bc23e;
    transform: translateY(-2px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Summary = styled.div`
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.borderSubtle};
  border-radius: ${theme.radius.lg};
  padding: 2rem;
  height: fit-content;
  position: sticky;
  top: 7rem;
`;
const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;
const SummaryImg = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: ${theme.radius.md};
`;
const SummaryInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  span {
    font-size: 0.88rem;
    color: ${theme.colors.textPrimary};
  }
  small {
    font-size: 0.74rem;
    color: ${theme.colors.textMuted};
    font-family: ${theme.fonts.mono};
  }
`;
const SummaryPrice = styled.span`
  font-family: ${theme.fonts.mono};
  font-size: 0.85rem;
  color: #89d146;
`;
const SummaryDivider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.borderSubtle};
  margin: 0.75rem 0;
`;
const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: ${theme.colors.textSecondary};
  margin: 6px 0;
`;
const ShippingVal = styled.span`
  color: ${({ free }) => (free ? "#89d146" : theme.colors.textSecondary)};
  font-family: ${theme.fonts.mono};
`;
const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1rem;
  color: ${theme.colors.textPrimary};
  font-weight: 600;
  margin-top: 4px;
`;
const FreeShippingNote = styled.p`
  font-size: 0.75rem;
  color: #89d146;
  font-family: ${theme.fonts.mono};
  margin-top: 1rem;
  text-align: center;
  letter-spacing: 0.06em;
`;
const SuccessBox = styled.div`
  max-width: 500px;
  margin: 10vh auto;
  text-align: center;
  h2 {
    font-family: "Playfair Display", serif;
    color: ${theme.colors.textPrimary};
    font-size: 2rem;
    margin: 1rem 0 0.5rem;
  }
  p {
    color: ${theme.colors.textSecondary};
    margin-bottom: 2rem;
  }
`;
const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(137, 209, 70, 0.15);
  border: 2px solid #89d146;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #89d146;
  margin: 0 auto;
`;
const BackBtn = styled.button`
  background: none;
  border: 1px solid ${theme.colors.borderSubtle};
  color: ${theme.colors.textSecondary};
  padding: 0.75rem 1.5rem;
  border-radius: 999px;
  cursor: pointer;
  font-family: ${theme.fonts.mono};
  font-size: 0.8rem;
  &:hover {
    border-color: #89d146;
    color: #89d146;
  }
`;
